<script setup lang="ts">
import { reactive } from "vue";

import {
  scratchpadConflictKey as key,
  describeScratchpadConflict as describe,
  type ScratchpadChoice,
  type ScratchpadConflict,
} from "@recordsure/unity-scratchpad/merge";

defineProps<{ conflicts: ScratchpadConflict[]; disabled: boolean }>();

const emit = defineEmits<{ confirm: [choices: ReadonlyMap<string, ScratchpadChoice>] }>();

const choices = reactive(new Map<string, ScratchpadChoice>());
</script>
<template>
  <div
    role="alertdialog"
    aria-labelledby="scratchpad-conflicts-title"
    tabindex="0"
    class="tw-absolute tw-inset-0 tw-flex tw-flex-col tw-gap-2 tw-overflow-auto tw-bg-white/95 tw-p-3 tw-text-xs tw-backdrop-blur-sm"
    @vue:mounted="({ el }: { el: HTMLElement }) => el.focus()"
  >
    <h3 id="scratchpad-conflicts-title" class="tw-m-0 tw-text-sm tw-font-semibold">Changed in another tab or device</h3>
    <p class="tw-m-0">Choose which version to keep for each change.</p>
    <div class="tw-flex tw-gap-2">
      <button
        type="button"
        class="tw-btn-md-gray"
        :disabled
        @click="conflicts.forEach((conflict) => choices.set(key(conflict), 'mine'))"
      >
        Use this tab for all conflicts
      </button>
      <button
        type="button"
        class="tw-btn-md-gray"
        :disabled
        @click="conflicts.forEach((conflict) => choices.set(key(conflict), 'theirs'))"
      >
        Use other version for all conflicts
      </button>
    </div>
    <fieldset
      v-for="(conflict, index) in conflicts"
      :key="key(conflict)"
      :disabled
      class="tw-flex tw-flex-col tw-gap-1 tw-rounded tw-border tw-p-2"
    >
      <legend class="tw-max-w-full tw-truncate tw-px-1 tw-font-semibold">
        {{
          conflict.type === "summary"
            ? "Case summary"
            : conflict.type === "clear"
              ? "Clear scratchpad"
              : `Conflict ${index + 1}`
        }}
      </legend>
      <label v-for="choice in ['mine', 'theirs'] as const" :key="choice" class="tw-flex tw-items-start tw-gap-2">
        <input
          type="radio"
          :name="key(conflict)"
          :checked="choices.get(key(conflict)) === choice"
          @change="choices.set(key(conflict), choice)"
        />
        <span class="tw-flex tw-flex-col">
          <span class="tw-font-semibold">{{ choice === "mine" ? "This tab" : "Other version" }}</span>
          <span v-for="line in describe(conflict, choice)" :key="line" class="tw-whitespace-pre-wrap">{{ line }}</span>
        </span>
      </label>
    </fieldset>
    <slot />
    <button
      type="button"
      class="tw-btn-md-green tw-shrink-0 tw-self-end"
      :disabled="disabled || !conflicts.every((conflict) => choices.has(key(conflict)))"
      @click="emit('confirm', choices)"
    >
      Save choices
    </button>
  </div>
</template>
